<template>
  <div class="community-page">
    <!-- 페이지 헤더 (타이틀 + 글쓰기) -->
    <div class="page-header">
      <h1 class="page-title">커뮤니티</h1>
      <button @click="handleWrite" class="write-btn">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        글쓰기
      </button>
    </div>

    <!-- 카테고리 탭 -->
    <div class="category-tabs">
      <button
        v-for="cat in categories"
        :key="cat.id"
        :class="['category-tab', { active: selectedCategory === cat.id }]"
        @click="selectCategory(cat.id)"
      >
        {{ cat.label }}
      </button>
    </div>

    <!-- 검색바 -->
    <div class="search-bar">
      <div class="search-input-wrapper">
        <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="검색어를 입력하세요"
          class="search-input"
          @keyup.enter="handleSearch"
        />
        <button v-if="searchQuery" @click="clearSearch" class="search-clear">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- 핫한 게시물 섹션 -->
    <div v-if="hotPosts.length > 0" class="hot-section">
      <div class="hot-header">
        <svg class="hot-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 23c-1.03 0-1.9-.5-2.6-1.1-.7-.6-1.3-1.4-1.7-2.4-.9-2-1-4.3-.4-6.4.5-1.9 1.5-3.6 2.8-5 .4-.4.8-.8 1.3-1.1-.1.7-.1 1.4 0 2.1.1 1.1.5 2.1 1.1 3 .3-.6.7-1.2 1.1-1.7 1-1.3 2.2-2.4 3.6-3.3.5-.3 1-.6 1.5-.8-.3 1.1-.4 2.2-.3 3.4.1 1.4.6 2.8 1.3 4 .7 1.2 1.6 2.3 2.6 3.2-.5.5-1.1.9-1.7 1.2-1.6.8-3.3 1.2-5.1 1.2-1.2 0-2.4-.2-3.5-.4z"/>
        </svg>
        <span class="hot-title">HOT</span>
      </div>
      <div class="hot-list">
        <div
          v-for="(post, index) in hotPosts"
          :key="post.id"
          class="hot-item"
          @click="goToDetail(post.id)"
        >
          <span class="hot-rank">{{ index + 1 }}</span>
          <span class="hot-post-title">{{ post.title }}</span>
        </div>
      </div>
    </div>

    <!-- 게시글 목록 -->
    <div class="post-list">
      <!-- 로딩 -->
      <div v-if="loading && posts.length === 0" class="loading-state">
        <div class="loading-spinner"></div>
        <p>게시글을 불러오는 중...</p>
      </div>

      <!-- 빈 상태 -->
      <div v-else-if="posts.length === 0" class="empty-state">
        <svg class="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
        <p>아직 게시글이 없습니다.</p>
        <p class="empty-sub">첫 번째 글을 작성해보세요!</p>
      </div>

      <!-- 게시글 아이템 -->
      <template v-for="(post, index) in posts" :key="post.id">
        <!-- 광고 게시글 (2번째 게시글 이후에 노출) -->
        <div
          v-if="communityAd && index === 2"
          class="post-item ad-post"
          @click="handleAdClick"
        >
          <div class="post-header">
            <div class="ad-badge-wrapper">
              <span class="ad-badge">AD</span>
              <span class="ad-property">{{ communityAd.propertyName }}</span>
            </div>
            <span class="post-time">광고</span>
          </div>
          <h3 class="post-title">{{ communityAd.title }}</h3>
          <p class="post-content">{{ truncateContent(communityAd.content) }}</p>
          <div class="post-footer">
            <span class="post-author ad-author">{{ communityAd.partnerDisplayName }}</span>
            <div class="ad-cta">
              자세히 보기
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
        <!-- 블라인드 처리된 게시글 -->
        <div v-if="post.isBlinded" class="post-item blinded">
          <p class="blinded-text">신고로 인해 숨겨진 게시글입니다.</p>
        </div>
        <!-- 일반 게시글 -->
        <div v-else class="post-item" @click="goToDetail(post.id)">
          <div class="post-header">
            <span class="post-category">{{ getCategoryLabel(post.category) }}</span>
            <span class="post-time">{{ post.timeAgo }}</span>
          </div>
          <h3 class="post-title">{{ post.title }}</h3>
          <p class="post-content">{{ truncateContent(post.content) }}</p>
          <div class="post-footer">
            <span class="post-author">{{ post.maskedEmail }}</span>
            <div class="post-stats">
              <span class="stat">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {{ post.viewCount || 0 }}
              </span>
              <span class="stat">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                {{ post.likeCount || 0 }}
              </span>
              <span class="stat">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {{ post.commentCount || 0 }}
              </span>
            </div>
          </div>
        </div>
      </template>

      <!-- 더보기 버튼 -->
      <div v-if="hasMore && !loading" class="load-more">
        <button @click="loadMore" class="load-more-btn">
          더보기
        </button>
      </div>

      <!-- 더 로딩중 -->
      <div v-if="loading && posts.length > 0" class="loading-more">
        <div class="loading-spinner small"></div>
      </div>
    </div>

    <!-- 로그인 필요 모달 -->
    <div v-if="showLoginModal" class="modal-overlay" @click="showLoginModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h3 class="modal-title">로그인이 필요합니다</h3>
        <p class="modal-desc">글을 작성하려면 로그인해주세요.</p>
        <div class="modal-buttons">
          <button @click="showLoginModal = false" class="modal-btn cancel">취소</button>
          <button @click="goToLogin" class="modal-btn confirm">로그인</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useCommunity, COMMUNITY_CATEGORIES } from '@/composables/main/useCommunity'
import { useCommunityAd } from '@/composables/partners/useCommunityAd'
import { useWeeklyRanking } from '@/composables/main/useWeeklyRanking'
import { useAuth } from '@/composables/common/useAuth'

const router = useRouter()
const { getPosts, loading } = useCommunity()
const { getRandomAd, incrementClick, incrementImpression } = useCommunityAd()
const { getCommunityHotPosts } = useWeeklyRanking()
const { isLoggedIn } = useAuth()

const categories = COMMUNITY_CATEGORIES
const selectedCategory = ref('all')
const searchQuery = ref('')
const posts = ref([])
const lastDoc = ref(null)
const hasMore = ref(false)
const showLoginModal = ref(false)

// 광고
const communityAd = ref(null)

// 핫한 게시물
const hotPosts = ref([])

// 카테고리 라벨 가져오기
const getCategoryLabel = (categoryId) => {
  const cat = categories.find(c => c.id === categoryId)
  return cat ? cat.label : categoryId
}

// 내용 자르기
const truncateContent = (content, maxLength = 100) => {
  if (!content) return ''
  if (content.length <= maxLength) return content
  return content.slice(0, maxLength) + '...'
}

// 광고 로드
const loadCommunityAd = async () => {
  try {
    communityAd.value = await getRandomAd()
    // 노출 수 증가
    if (communityAd.value) {
      incrementImpression(communityAd.value.id)
    }
  } catch (err) {
    console.error('커뮤니티 광고 로드 실패:', err)
  }
}

// 핫한 게시물 로드
const loadHotPosts = async () => {
  try {
    hotPosts.value = await getCommunityHotPosts(5)
  } catch (err) {
    console.error('핫한 게시물 로드 실패:', err)
  }
}

// 광고 클릭 처리
const handleAdClick = () => {
  if (!communityAd.value) return
  incrementClick(communityAd.value.id)
  // 캠페인 랜딩 페이지로 이동 시 ?from=community 추가
  const url = communityAd.value.linkUrl
  const separator = url.includes('?') ? '&' : '?'
  router.push(`${url}${separator}from=community`)
}

// 게시글 로드
const loadPosts = async (reset = false) => {
  if (reset) {
    posts.value = []
    lastDoc.value = null
  }

  const result = await getPosts({
    category: selectedCategory.value,
    pageSize: 20,
    lastDoc: lastDoc.value,
    searchQuery: searchQuery.value
  })

  if (reset) {
    posts.value = result.posts
  } else {
    posts.value = [...posts.value, ...result.posts]
  }

  lastDoc.value = result.lastDoc
  hasMore.value = result.hasMore
}

// 카테고리 선택
const selectCategory = (categoryId) => {
  selectedCategory.value = categoryId
  loadPosts(true)
}

// 검색
const handleSearch = () => {
  loadPosts(true)
}

// 검색어 초기화
const clearSearch = () => {
  searchQuery.value = ''
  loadPosts(true)
}

// 더보기
const loadMore = () => {
  loadPosts(false)
}

// 상세 페이지로 이동
const goToDetail = (postId) => {
  router.push(`/community/${postId}`)
}

// 글쓰기 처리
const handleWrite = () => {
  if (isLoggedIn.value) {
    router.push('/community/write')
  } else {
    showLoginModal.value = true
  }
}

// 로그인 페이지로 이동
const goToLogin = () => {
  showLoginModal.value = false
  router.push('/login')
}

onMounted(() => {
  loadPosts(true)
  loadCommunityAd()
  loadHotPosts()
})
</script>

<style scoped>
.community-page {
  min-height: 100vh;
  background: #ffffff;
  padding-bottom: 80px;
}

/* 페이지 헤더 */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  max-width: 480px;
  margin: 0 auto;
  background: white;
}

.page-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.write-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: #2563eb;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.write-btn:hover {
  background: #1d4ed8;
}

/* 핫한 게시물 섹션 */
.hot-section {
  background: white;
  padding: 12px 16px;
  max-width: 480px;
  margin: 0 auto;
  border-bottom: 1px solid #e2e8f0;
}

.hot-header {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 10px;
}

.hot-icon {
  width: 16px;
  height: 16px;
  color: #ef4444;
}

.hot-title {
  font-size: 13px;
  font-weight: 700;
  color: #ef4444;
}

.hot-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.hot-item {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.hot-item:active {
  opacity: 0.7;
}

.hot-rank {
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: white;
  background: #ef4444;
  border-radius: 4px;
  flex-shrink: 0;
}

.hot-item:nth-child(1) .hot-rank {
  background: #ef4444;
}

.hot-item:nth-child(2) .hot-rank {
  background: #f97316;
}

.hot-item:nth-child(3) .hot-rank {
  background: #eab308;
}

.hot-item:nth-child(n+4) .hot-rank {
  background: #94a3b8;
}

.hot-post-title {
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.write-btn svg {
  width: 16px;
  height: 16px;
}

/* 카테고리 탭 */
.category-tabs {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  max-width: 480px;
  margin: 0 auto;
  background: white;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.category-tabs::-webkit-scrollbar {
  display: none;
}

.category-tab {
  flex-shrink: 0;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  background: #f1f5f9;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.category-tab.active {
  color: white;
  background: #2563eb;
}

/* 검색바 */
.search-bar {
  padding: 12px 16px;
  max-width: 480px;
  margin: 0 auto;
  background: white;
  border-bottom: 1px solid #e2e8f0;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #f1f5f9;
  border-radius: 12px;
}

.search-icon {
  width: 18px;
  height: 18px;
  color: #94a3b8;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 14px;
  color: #1e293b;
  outline: none;
}

.search-input::placeholder {
  color: #94a3b8;
}

.search-clear {
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: none;
  color: #94a3b8;
  cursor: pointer;
}

.search-clear svg {
  width: 16px;
  height: 16px;
}

/* 게시글 목록 */
.post-list {
  padding: 12px 16px;
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.post-item {
  background: white;
  padding: 16px;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s;
}

.post-item:active {
  background: #f8fafc;
  transform: scale(0.98);
}

.post-item.blinded {
  cursor: default;
  background: #f8fafc;
}

.blinded-text {
  font-size: 14px;
  color: #94a3b8;
  text-align: center;
  margin: 0;
  padding: 8px 0;
}

.post-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.post-category {
  font-size: 11px;
  font-weight: 600;
  color: #2563eb;
  padding: 2px 8px;
  background: #dbeafe;
  border-radius: 4px;
}

.post-time {
  font-size: 12px;
  color: #94a3b8;
}

.post-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 6px 0;
  line-height: 1.4;
}

.post-content {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 12px 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.post-author {
  font-size: 12px;
  color: #94a3b8;
}

.post-stats {
  display: flex;
  align-items: center;
  gap: 12px;
}

.stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #94a3b8;
}

.stat svg {
  width: 14px;
  height: 14px;
}

/* 광고 게시글 - 일반 게시글과 동일한 스타일 */
.ad-post {
  background: white;
}

.ad-post:active {
  background: #f8fafc;
}

.ad-badge-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ad-badge {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  padding: 2px 6px;
  background: #f1f5f9;
  border-radius: 4px;
}

.ad-property {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
}

.ad-author {
  color: #94a3b8;
}

.ad-cta {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
}

.ad-cta svg {
  width: 14px;
  height: 14px;
}

/* 로딩 상태 */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #94a3b8;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 12px;
}

.loading-spinner.small {
  width: 24px;
  height: 24px;
  border-width: 2px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 12px;
  color: #cbd5e1;
}

.empty-state p {
  font-size: 14px;
  margin: 0;
}

.empty-sub {
  color: #94a3b8;
  margin-top: 4px !important;
}

/* 더보기 */
.load-more {
  padding: 16px;
  text-align: center;
}

.load-more-btn {
  padding: 12px 32px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.load-more-btn:hover {
  background: #f8fafc;
}

.loading-more {
  display: flex;
  justify-content: center;
  padding: 16px;
}

/* 모달 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 20px;
}

.modal-content {
  width: 100%;
  max-width: 320px;
  background: white;
  border-radius: 20px;
  padding: 32px 24px 24px;
  text-align: center;
}

.modal-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  background: #dbeafe;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-icon svg {
  width: 28px;
  height: 28px;
  color: #2563eb;
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.modal-desc {
  font-size: 14px;
  color: #64748b;
  margin: 0 0 24px 0;
}

.modal-buttons {
  display: flex;
  gap: 12px;
}

.modal-btn {
  flex: 1;
  padding: 14px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.modal-btn.cancel {
  color: #64748b;
  background: #f1f5f9;
}

.modal-btn.confirm {
  color: white;
  background: #2563eb;
}

.modal-btn:active {
  transform: scale(0.98);
}
</style>
